// =============================================================================
// hestia/organigramme.js : directeurs de domaine et coordinateurs de pôle,
// écrits par le serveur SEUL.
// -----------------------------------------------------------------------------
// Les directeurs de domaine et les coordinateurs de pôle de l'organigramme
// d'Enio (`gouvernance/enio.orgEnio.domaines`) sont habilités à Hestia par
// défaut, et une désignation y ouvre ou y ferme les droits (HO2, second tour
// du DG d'Enio, 01/10/2026). Leur écriture est donc réservée aux
// administrateurs, et passe par une action de hestiaAdmin, jamais par un
// navigateur (règle firestore : `orgEnio.domaines` comparé avant et après) :
//   designerOrganigramme       directeur d'un domaine, coordinateur d'un pôle ;
//   renommerPoleOrganigramme   la désignation SUIT le pôle renommé, quand il
//                              en porte une (avant le lot H1, le renommage
//                              écrivait `gouvernance/orgEnio`, que personne ne
//                              lit, et la désignation restait sous l'ancien
//                              nom).
// DÉCOUPLAGE (04/10/2026) : les postes de l'organigramme de Gouvernance (dg,
// dops, adjops, attdir, dga1, dga2 ; « dg » est lu par signatairesConvocation)
// et le renommage d'un pôle SANS désignation n'ouvrent aucun droit dans
// Hestia : ils restent sur le chemin d'Aramis (navigateur d'un administrateur,
// règle de gouvernance/), sans dépendre d'Hestia ni de son interrupteur.
// Module pur : (orgEnio, params, { fiches, poleRenames }) → { orgEnio, cible,
// avant, apres, inchange } | { erreur }. Il garde TOUT le reste de
// l'organigramme tel quel (clés inconnues et horodatages compris : copie
// FIDÈLE, jamais un aller-retour JSON) et compare ce que l'écran a montré
// (`avant`) à ce qui est en base : une écriture arrivée entre-temps est
// refusée, jamais écrasée. Les clés `__proto__`, `constructor` et `prototype`
// ne sont jamais un nom de pôle (lecture par Object.hasOwn, jamais par `in`).
// Ses messages sont ceux d'Aramis (l'éditeur et le renommage vivent dans
// Domaines & Pôles) : aucun ne parle d'Hestia. Copié pour le serveur par
// scripts/sync-hestia-serveur.js.
// =============================================================================
import { ficheEligible } from "./mutuelles";

/** Les domaines et leurs pôles PAR DÉFAUT : miroir des identifiants et des
 *  pôles d'ORG_DOMAINES (src/appShared.js), vérifié par hestiaOrganigramme.test.js.
 *  Un pôle renommé garde sa clé par défaut dans config/settings.poleRenames. */
export const DOMAINES_ORGANIGRAMME = Object.freeze([
  { id: "dev", poles: ["Offres, distribution et marketing", "Gestion & Relation adhérents", "Cellule de réponse aux Appels d'offres"] },
  { id: "aff", poles: ["Relations extérieures et partenariats", "Communication et presse", "Affaires publiques"] },
  { id: "imp", poles: ["RSE (Responsabilité Sociale de l'Entreprise)", "Prévention et juste soin", "Innovation & investissements stratégiques"] },
  { id: "pil", poles: ["Actuariat et pilotage de la donnée", "Conformité, Juridique et Protection des données",
    "Gestion des risques, contrôle interne et audit interne", "Administration, Comptabilité & finance"] },
  { id: "res", poles: ["Ressources humaines", "Informatique, outils et sécurité", "Négociation & Acquisition (Achats)"] },
]);
export const NOM_POLE_MAX = 80;
export const ACTIONS_ORGANIGRAMME = Object.freeze(["designerOrganigramme", "renommerPoleOrganigramme"]);
/** Jamais un nom de pôle ni une clé d'organigramme (prototype des objets). */
export const CLES_INTERDITES = Object.freeze(["__proto__", "constructor", "prototype"]);

const erreur = (code, message) => ({ erreur: { code, message } });
const nomPropre = (s) => String(s == null ? "" : s).replace(/\s+/g, " ").trim();
const estObjet = (x) => !!x && typeof x === "object" && !Array.isArray(x);
const idOuNull = (v) => (v == null || v === "" ? null : String(v));
const interdite = (k) => CLES_INTERDITES.includes(String(k).toLowerCase());
/** La clé est-elle PROPRE à l'objet (Object.hasOwn : jamais le prototype) ? */
const aCle = (o, k) => estObjet(o) && !interdite(k) && Object.prototype.hasOwnProperty.call(o, k);
const lire = (o, k) => (aCle(o, k) ? o[k] : undefined);
/** Un objet littéral (ni horodatage Firestore, ni autre instance de classe). */
const estLitteral = (x) => estObjet(x) && (Object.getPrototypeOf(x) === Object.prototype || Object.getPrototypeOf(x) === null);
/** Copie FIDÈLE : objets littéraux et tableaux copiés, toute autre valeur
 *  (horodatage Firestore, référence…) gardée telle quelle. Un aller-retour
 *  JSON changeait un horodatage en carte { _seconds, _nanoseconds }. */
export function copieFidele(x) {
  if (Array.isArray(x)) return x.map(copieFidele);
  if (!estLitteral(x)) return x;
  const out = {};
  for (const k of Object.keys(x)) if (x[k] !== undefined && !interdite(k)) out[k] = copieFidele(x[k]);
  return out;
}
const copie = (x) => (estObjet(x) ? copieFidele(x) : {});
const CONFLIT = "L'organigramme a changé entre-temps : rechargez la page avant de recommencer.";
const FICHE = "La personne désignée doit avoir une fiche active, ni invitée ni technique.";

export const domaineOrganigramme = (id) => DOMAINES_ORGANIGRAMME.find((d) => d.id === id) || null;

/** Les noms de pôle renommés (config/settings.poleRenames : nom par défaut →
 *  nom du moment), relus champ par champ ; jamais une clé ni un nom interdits. */
export function renommagesPropres(r) {
  if (!estObjet(r)) return {};
  const out = {};
  for (const k of Object.keys(r)) {
    const v = r[k];
    if (interdite(k) || typeof v !== "string" || interdite(nomPropre(v))) continue;
    if (nomPropre(v) && k.length <= 160 && v.length <= 160) out[k] = nomPropre(v);
  }
  return out;
}

/** Les pôles d'un domaine, sous leur nom du moment (même règle que l'écran
 *  Pôles & Domaines : un nom par défaut, puis son renommage). */
export function polesEffectifs(domaineId, poleRenames) {
  const d = domaineOrganigramme(domaineId);
  const r = renommagesPropres(poleRenames);
  return d ? d.poles.map((p) => lire(r, p) || p) : [];
}

/** Les renommages après « ancien → nouveau » : la clé est le nom PAR DÉFAUT
 *  du pôle (avant ce lot, un second renommage écrivait `ancien → nouveau` à
 *  côté de `défaut → ancien`, que l'écran lit seul : le pôle gardait son
 *  premier nouveau nom, et perdait ses membres, renommés ailleurs). */
export function renommagesApres(poleRenames, domaineId, ancien, nouveau) {
  const r = renommagesPropres(poleRenames);
  const d = domaineOrganigramme(domaineId);
  const defaut = d ? d.poles.find((p) => (lire(r, p) || p) === ancien) : null;
  return { ...r, [defaut || ancien]: nouveau };
}

/** Le nom d'un pôle nouveau : propre, borné, sans doublon avec un pôle du
 *  moment (tous domaines), jamais un nom réservé. → { nom } | { erreur: message } */
export function nomPoleValide(nouveau, ancien, poleRenames) {
  const nom = nomPropre(nouveau);
  if (!nom) return { erreur: "Le nouveau nom du pôle est vide." };
  if (nom.length > NOM_POLE_MAX) return { erreur: `Un nom de pôle compte ${NOM_POLE_MAX} caractères au plus.` };
  if (interdite(nom)) return { erreur: "Ce nom de pôle n'est pas permis : choisissez-en un autre." };
  if (nom === nomPropre(ancien)) return { erreur: "Le nouveau nom est celui du pôle." };
  const pris = DOMAINES_ORGANIGRAMME.flatMap((d) => polesEffectifs(d.id, poleRenames));
  if (pris.some((p) => p.toLowerCase() === nom.toLowerCase())) return { erreur: `Un pôle s'appelle déjà « ${nom} ».` };
  return { nom };
}

/** Le directeur d'un domaine ou le coordinateur d'un pôle (identifiant, ou null). */
export function responsable(orgEnio, domaineId, pole = null) {
  const d = lire(lire(orgEnio, "domaines"), domaineId);
  if (!estObjet(d)) return null;
  if (!pole) return idOuNull(d.coordinateurId);
  const p = lire(d.poles, pole);
  return estObjet(p) ? idOuNull(p.coordinateurId) : null;
}

/** Le pôle porte-t-il une désignation (un coordinateur) ? Le renommage ne
 *  passe par le serveur que dans ce cas (PolesHubModule). */
export const poleDesigne = (orgEnio, domaineId, pole) => responsable(orgEnio, domaineId, pole) !== null;

function personneValide(id, fiches) {
  if (id === null) return true;
  const f = (Array.isArray(fiches) ? fiches : []).find((x) => x && x.id === id);
  return ficheEligible(f);
}

/** Désigner (ou retirer : delegateId vide) le directeur d'un domaine, ou le
 *  coordinateur d'un pôle. params : { domaine, pole?, delegateId, avant }.
 *  `avant` et `apres` du résultat : les IDENTIFIANTS (jamais des noms), pour
 *  le journal. */
export function designerResponsable(orgEnio, p, { fiches, poleRenames } = {}) {
  const d = domaineOrganigramme(p && p.domaine);
  if (!d) return erreur("invalide", "Domaine inconnu.");
  const pole = p.pole == null || p.pole === "" ? null : String(p.pole);
  if (pole !== null && interdite(pole)) return erreur("invalide", "Ce pôle n'existe pas dans ce domaine.");
  const nouveau = idOuNull(p.delegateId);
  // Une désignation restée sous un nom de pôle qui n'existe plus (renommage
  // d'avant ce lot, ou interrompu) compte encore dans Hestia : elle se RETIRE
  // ici (la clé disparaît), elle ne se pose jamais.
  const orpheline = !!pole && !polesEffectifs(d.id, poleRenames).includes(pole);
  if (orpheline && !(nouveau === null && responsable(orgEnio, d.id, pole) !== null)) return erreur("invalide", "Ce pôle n'existe pas dans ce domaine.");
  const enBase = responsable(orgEnio, d.id, pole);
  if (enBase !== idOuNull(p.avant)) return erreur("conflit", CONFLIT);
  if (!personneValide(nouveau, fiches)) return erreur("invalide", FICHE);
  const cible = pole ? `${d.id} / ${pole}` : d.id;
  if (nouveau === enBase) return { orgEnio, cible, avant: enBase, apres: nouveau, inchange: true };
  const org = copie(orgEnio);
  org.domaines = estObjet(org.domaines) ? org.domaines : {};
  const dom = estObjet(lire(org.domaines, d.id)) ? org.domaines[d.id] : {};
  if (orpheline) {
    delete dom.poles[pole];
  } else if (pole) {
    const poles = estObjet(dom.poles) ? dom.poles : {};
    poles[pole] = { ...(estObjet(lire(poles, pole)) ? poles[pole] : {}), coordinateurId: nouveau };
    dom.poles = poles;
  } else {
    dom.coordinateurId = nouveau;
  }
  org.domaines[d.id] = dom;
  return { orgEnio: org, cible, avant: enBase, apres: nouveau, inchange: false };
}

/** La désignation d'un pôle suit son renommage. Idempotent : rejoué après un
 *  renommage interrompu (la désignation déjà déplacée), il ne fait rien.
 *  params : { domaine, ancien, nouveau }. */
export function renommerPole(orgEnio, p, { poleRenames } = {}) {
  const d = domaineOrganigramme(p && p.domaine);
  if (!d) return erreur("invalide", "Domaine inconnu.");
  const ancien = nomPropre(p.ancien);
  if (!polesEffectifs(d.id, poleRenames).includes(ancien)) return erreur("invalide", "Ce pôle n'existe pas dans ce domaine.");
  const v = nomPoleValide(p.nouveau, ancien, poleRenames);
  if (v.erreur) return erreur("invalide", v.erreur);
  const cible = `${d.id} / ${ancien} → ${v.nom}`;
  const poles = lire(lire(lire(orgEnio, "domaines"), d.id), "poles");
  const qui = responsable(orgEnio, d.id, ancien);
  if (!aCle(poles, ancien)) return { orgEnio, cible, avant: null, apres: null, inchange: true };
  if (aCle(poles, v.nom)) return erreur("conflit", `Le pôle « ${v.nom} » porte déjà une désignation dans ce domaine : rechargez la page.`);
  const org = copie(orgEnio);
  const suivants = { ...org.domaines[d.id].poles };
  suivants[v.nom] = suivants[ancien];
  delete suivants[ancien];
  org.domaines[d.id].poles = suivants;
  return { orgEnio: org, cible, avant: qui, apres: qui, inchange: false };
}

/** Les désignations restées sous un nom de pôle qui n'existe plus dans le
 *  domaine (polesDuMoment : les noms que l'écran montre). Pur. */
export function designationsOrphelines(polesDesignes, polesDuMoment) {
  const connus = new Set(polesDuMoment || []);
  return (Array.isArray(polesDesignes) ? polesDesignes : []).filter((x) => x && !connus.has(x.nom));
}

/** Point d'entrée des deux actions (actions.js) :
 *  → { orgEnio, cible, avant, apres, inchange } | { erreur }. */
export function modifierOrganigramme(action, orgEnio, params, ctx) {
  const p = params || {};
  if (action === "designerOrganigramme") return designerResponsable(orgEnio, p, ctx);
  if (action === "renommerPoleOrganigramme") return renommerPole(orgEnio, p, ctx);
  return erreur("invalide", "Action inconnue.");
}

/** Le refus d'un administrateur que la porte retient encore (droits des
 *  fiches pas encore déclarés protégés) : le même dans l'action et dans la vue
 *  de l'éditeur, en mots d'Aramis. */
export const ORGANIGRAMME_RETENU = "Les directeurs de domaine et les coordinateurs de pôle se modifient, pour l'instant, par le super-administrateur seul.";
/** L'organigramme d'Enio est momentanément indisponible : les mots d'Aramis,
 *  jamais un message d'Hestia, sur les pages d'Aramis (Domaines & Pôles). */
export const ORGANIGRAMME_INDISPONIBLE = "L'organigramme d'Enio est momentanément indisponible : réessayez plus tard.";
/** Un renommage dont la désignation a suivi, mais dont le reste a échoué :
 *  RELANCER le même renommage le termine (le serveur ne refait rien). */
export const RENOMMAGE_A_RELANCER = "Le coordinateur du pôle a suivi le nouveau nom, mais le renommage ne s'est pas terminé : relancez-le à l'identique, il reprendra là où il s'est arrêté.";

/** Le renommage d'un pôle qui a un coordinateur, refusé à un administrateur
 *  que la porte retient encore : dit que c'est le RENOMMAGE qui est refusé,
 *  et pourquoi (relecture du 04/10/2026). */
export const RENOMMAGE_RETENU = "Ce pôle a un coordinateur : son renommage passe, pour l'instant, par le super-administrateur.";

/** Les codes d'un refus de la porte de l'organigramme (transportServeur.js,
 *  erreurAppel) : leur message est un texte écrit pour l'écran. */
const CODES_DE_LA_PORTE = new Set(["refuse", "invalide", "conflit", "introuvable"]);

/** Le message d'un refus de l'organigramme, en mots d'Aramis : une panne,
 *  l'interrupteur de secours ou un texte qui nommerait Hestia deviennent
 *  « momentanément indisponible » ; toute autre erreur (une lecture
 *  Firestore hors ligne, un morceau qui ne se charge pas : des messages
 *  bruts, en anglais) rend le repli. Pur. */
export function messageOrganigramme(e, repli = "L'organigramme d'Enio n'a pas pu être enregistré : réessayez dans un instant.") {
  const code = e && e.code;
  if (code === "indisponible" || code === "reseau") return ORGANIGRAMME_INDISPONIBLE;
  if (!CODES_DE_LA_PORTE.has(code)) return repli;
  const m = e && typeof e.message === "string" ? e.message : "";
  if (!m || /hestia/i.test(m)) return repli;
  return m;
}
